/**
 * @fileoverview Change-password dialog opened from SettingsPage.
 * @description Wraps ChangePasswordForm in the shared Modal so a voluntary password change happens
 * in place on Settings instead of on its own page. ChangePasswordForm owns all the resettable state
 * (its form values and mutation) and is rendered as Modal's children, which Modal stops rendering
 * the moment `open` turns false — so closing the dialog unmounts the form and every reopen starts
 * clean, with no explicit reset needed. A successful change signs the user out (see
 * useChangePassword), so there's no "success" state to show here either. The forced change after
 * sign-in does not use this dialog — see ChangePasswordCard.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import ChangePasswordForm from '@/features/auth/components/ChangePasswordForm'
import Modal from '@/shared/components/molecules/Modal'

interface ChangePasswordModalProps {
  open: boolean
  onClose: () => void
}

/**
 * @component
 * @param {ChangePasswordModalProps} props - Open state and close handler.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called on Escape, a backdrop click, or the close button.
 */
function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation()

  return (
    <Modal open={open} onClose={onClose} title={t('settings.changePassword.title')}>
      <ChangePasswordForm />
    </Modal>
  )
}

export default ChangePasswordModal
